// TESSERAX — MODE ATTRACT (ROADMAP §1 « wow en 10 secondes »). Cœur PUR : ni THREE ni DOM.
//
// L'écran d'accueil montrait une formation STATIQUE (core/showcase) : le joueur ne voyait
// RIEN du jeu — ni chute, ni clear, ni la Bascule signature — avant sa première partie.
// Ce module génère un JOURNAL D'ÉVÈNEMENTS déterministe « façon borne d'arcade » : le bot
// heuristique (core/bot, profil avancé, bruit nul) pilote le MÊME moteur que l'anti-triche
// (core/sim.feed), à un tempo resserré, avec des crans de GRAVITY synthétiques pour que la
// chute soit VISIBLE, et des Bascules périodiques pour que la mécanique signature (le
// tassement latéral de toute la pile) se produise sous les yeux du visiteur.
//
// Le journal produit se REJOUE ensuite dans le shell (boot.ts) via createSim + stepTo,
// rendu dans la pile instanciée de la vitrine — purement visuel : AUCUNE mutation de
// l'état de jeu, de la RNG de run ni du méta. Déterministe : mêmes (graine) → même journal
// (le bruit du bot est nul, sa RNG est séparée et seedée).

import { GRID, HEIGHT } from './constants';
import { bestBascule, previewBascule, type Dir } from './grid';
import { planPlacement, bestBasculeDir, holeCount, BOT_PROFILES, ROT_CODE, BASC_CODE } from './bot';
import { createSim } from './sim';
import { CODE, type InputEvent } from './replay';
import { mulberry32 } from './rng';

// ----- Tempo « attract » (ms) : plus vif que le jeu réel, mais assez lent pour être LU. -----
const INPUT_MS = 70;      // entre deux entrées (rotation / pas latéral)
const FALL_MS = 55;       // un cran de chute — la pièce DESCEND, elle ne se téléporte pas
const LOCK_MS = 90;       // verrou après l'atterrissage
const SETTLE_MS = 240;    // respiration après un verrou (laisse lire un éventuel clear)
const BASC_LEAD_MS = 320; // suspension AVANT une Bascule (annonce le geste)
const BASC_TAIL_MS = 720; // temps de lecture APRÈS (le glissement + la cascade)
const SKIP_MS = 260;      // offre de relique passée (invisible en vitrine)

const PIECES = 34;          // ≈ 35–45 s de boucle au tempo ci-dessus
const BASC_EVERY = 8;       // une Bascule « de démonstration » au moins toutes les N pièces
const MAX_GRAV = HEIGHT + 2; // garde-fou anti-boucle par pièce

const DIRS: Dir[] = ['nx', 'px', 'nz', 'pz'];

// Direction dont le glissement DÉPLACE le plus de blocs (la Bascule la plus SPECTACULAIRE,
// même sans clear) — pour la démo, montrer le geste prime sur l'optimiser.
function showiestBascule(grid: Parameters<typeof previewBascule>[0]): { dir: Dir; moved: number } | null {
  let best: { dir: Dir; moved: number } | null = null;
  for (let i = 0; i < DIRS.length; i++) {
    const p = previewBascule(grid, DIRS[i], undefined, false);
    if (!best || p.moved.length > best.moved) best = { dir: DIRS[i], moved: p.moved.length };
  }
  return best && best.moved >= 4 ? best : null;   // < 4 blocs déplacés : geste illisible, on s'abstient
}

// Journal d'attract pour une graine (celle du défi du jour → la vitrine se renouvelle chaque
// jour, comme la formation qu'elle remplace). Mode 'free' : endless, 1 charge de départ.
export function attractScript(seed: number): InputEvent[] {
  const out: InputEvent[] = [];
  const sim = createSim(seed >>> 0, 'free', out);   // feed() pousse dans `out` → `out` EST le journal
  const S = sim.state;
  const prof = BOT_PROFILES.avance;
  const rand = mulberry32((seed ^ 0x9e3779b9) >>> 0);   // flux séparé (n'altère pas les pièces)
  let t = 0;
  const feed = (code: number, dt: number) => { t += dt; sim.feed([[t, code]]); };
  let pieces = 0, sinceBascule = 0;

  while (S.running && pieces < PIECES) {
    // Offre de relique (niveaux pairs du mode libre) : passée — invisible en vitrine, et le
    // +1 charge du skip alimente les Bascules de démonstration.
    if (S.choosing) { feed(CODE.RELIC_SKIP, SKIP_MS); continue; }

    // ---- LE MOMENT SIGNATURE : Bascule quand une cascade est prête, ou périodiquement pour
    // montrer le geste (le tassement latéral de toute la pile est l'image de marque du jeu).
    if (S.charges > 0 && pieces > 2) {
      const ready = bestBascule(S.grid);                      // une cascade préparée ? toujours la jouer
      const due = sinceBascule >= BASC_EVERY;
      const repair = due && holeCount(S.grid) > 0 ? bestBasculeDir(S.grid) : null;
      const show = due && !ready && !repair ? showiestBascule(S.grid) : null;
      const dir = ready ? ready.dir : repair || (show ? show.dir : null);
      if (dir) { feed(BASC_CODE[dir], BASC_LEAD_MS); t += BASC_TAIL_MS; sinceBascule = 0; }
      if (!S.running || !S.current) break;
    }

    // ---- Placement piloté (profil avancé, bruit nul, budget d'inputs illimité : la démo est
    // un joueur idéal — c'est une bande-annonce, pas une mesure).
    const plan = S.current ? planPlacement(S.grid, { cells: S.current.cells, pos: S.current.pos }, prof.w, 0, rand) : null;
    if (plan) {
      for (let i = 0; i < plan.axes.length; i++) { feed(ROT_CODE[plan.axes[i]], INPUT_MS); if (!S.current) break; }
      if (S.current) {
        let minX = 1e9, minZ = 1e9;
        for (const c of S.current.cells) { const ax = c[0] + S.current.pos[0], az = c[2] + S.current.pos[2]; if (ax < minX) minX = ax; if (az < minZ) minZ = az; }
        let dx = plan.targetX - minX, dz = plan.targetZ - minZ;
        while (dx !== 0) { feed(dx > 0 ? CODE.MOVE_XP : CODE.MOVE_XN, INPUT_MS); dx += dx > 0 ? -1 : 1; }
        while (dz !== 0) { feed(dz > 0 ? CODE.MOVE_ZP : CODE.MOVE_ZN, INPUT_MS); dz += dz > 0 ? -1 : 1; }
      }
    }
    // ---- Chute VISIBLE : crans de gravité jusqu'au sol (boucle fermée sur la position réelle),
    // puis verrou. Le sim relit ces mêmes évènements à l'identique côté vitrine.
    for (let g = 0; g < MAX_GRAV && S.current; g++) {
      const y = S.current.pos[1];
      feed(CODE.GRAVITY, FALL_MS);
      if (!S.current || S.current.pos[1] === y) break;        // au sol (le cran n'a pas bougé)
    }
    feed(CODE.LOCK, LOCK_MS);
    t += SETTLE_MS;
    pieces++; sinceBascule++;
  }
  return out;
}
